// NIGHT-DIRECTSTAFF-2026-10-23 §1: the Reviews queue -- every review submitted on traveltherapyreviews.com (public.reviews,
// filled from the review_submission leads by migrations/2026-10-23-reviews.sql), with Approve / Reject (a reason
// required). Never an edit: the text is shown exactly as written. Approved and not rejected = published on the reviews
// site (public.reviews_published). Dark until the hub applies the migration (the table missing -> { ready:false }).

import { isTyping } from './keyboard';

export const REVIEW_LOG_TYPE = 'review';
export const REVIEW_COLS = 'id,lead_id,submitted_at,agency,agency_slug,rating,discipline,review_text,reviewer_name,approved_at,approved_by,rejected_at,rejected_by,reject_reason';
// NIGHT-DIRECTSTAFF-2026-10-28 §1: the spam score (2026-10-28-reviews-spam-score.sql; absent before it -> no score, no strip)
export const REVIEW_COLS_SPAM = `${REVIEW_COLS},spam_score,spam_reasons,spam_cleared_at,spam_cleared_by`;
export const SPAM_AT = 70;                                    // the database's "likely spam": a score of 70 or more
export const SPAM_REASON = 'Spam or a test';                  // Reject all as spam -- the reason staff already pick
export const SPAM_WORDS = { link: 'a link', name_is_email_or_link: 'the name is an email or a link', repeated_text: 'the same text as an earlier review',
  burst: 'another review from the same person within a minute', too_short: 'very short', too_long: 'very long', rating_without_text: 'a rating and almost no text',
  no_therapy_words: 'nothing about therapy or travel work', agency_not_known: 'an agency the site has no page for',
  // NIGHT-DIRECTSTAFF-2026-10-28c §1 (2026-10-29-reviews-spam-score-v2.sql): the two weak ones together add 25
  two_weak_signals: 'both of those together' };
/** a waiting review the database scored 70+ that staff have not said is not spam */
export const likelySpam = (r) => !!r && r.spam_score !== null && r.spam_score !== undefined && Number(r.spam_score) >= SPAM_AT && !r.spam_cleared_at
  && !r.approved_at && !r.rejected_at;
/** "a link · nothing about therapy or travel work" */
export const spamWords = (r) => ((r && r.spam_reasons) || []).map((k) => SPAM_WORDS[k] || String(k).replace(/_/g, ' ')).join(' · ');

// The reviews site's ten agency pages (traveltherapyreviews.com/reviews/<slug>/). Staff pin the page when approving; the
// agency text the reviewer typed is free text, so a likely page is preselected from it. None = published, on no agency page.
export const AGENCY_PAGES = [
  ['amn-healthcare', 'AMN Healthcare', /\bamn\b/i], ['aureus-medical', 'Aureus Medical', /aureus/i], ['comphealth', 'CompHealth', /comp\s*health/i],
  ['core-medical-group', 'Core Medical Group', /core\s*medical/i], ['cross-country-allied', 'Cross Country', /cross\s*country/i],
  ['fusion-medical-staffing', 'Fusion Medical Staffing', /fusion/i], ['host-healthcare', 'Host Healthcare', /\bhost\b/i],
  ['jackson-therapy-partners', 'Jackson Therapy Partners', /jackson/i], ['med-travelers', 'Med Travelers', /med\s*travel+ers/i],
  ['protherapy-staffing', 'ProTherapy Staffing', /pro\s*therapy/i],
].map(([slug, label, re]) => ({ slug, label, re }));
/** The agency page the reviewer's agency text most likely means, or '' (none of the ten). */
export const guessSlug = (agency) => (AGENCY_PAGES.find((a) => a.re.test(String(agency || ''))) || { slug: '' }).slug;
export const agencyPageLabel = (slug) => (AGENCY_PAGES.find((a) => a.slug === slug) || { label: '' }).label;
export const REASON_MAX = 500;
const missing = (e) => !!e && (/PGRST205|42P01/.test(String(e.code || '')) || /does not exist|could not find the table/i.test(String(e.message || '')));

/** 'pending' | 'approved' | 'rejected' */
export const reviewState = (r) => (r.rejected_at ? 'rejected' : r.approved_at ? 'approved' : 'pending');
export const STATE_LABELS = { pending: 'Waiting', approved: 'Published', rejected: 'Not published' };
/** §6 (10-24): the label shown -- an approved review the site's view does not have yet says so (never "Published" before it is) */
export const stateLabel = (r) => (reviewState(r) === 'approved' && r.published === false ? 'Approved, not on the site yet' : STATE_LABELS[reviewState(r)]);
/** the toast after Approve: what the publish step did */
export const publishWords = (p) => (p && p.dispatched ? 'Approved: the reviews site is publishing it now'
  : 'Approved: it goes up with the next reviews-site publish');
/** The filter chips: what's waiting (oldest first, so nothing sits), then everything decided (newest first). */
export const REVIEW_FILTERS = [{ id: 'pending', label: 'Waiting' }, { id: 'approved', label: 'Published' }, { id: 'rejected', label: 'Not published' }, { id: 'all', label: 'All' }];
export function shownReviews(rows = [], filter = 'pending') {
  const list = filter === 'all' ? rows : rows.filter((r) => reviewState(r) === filter);
  return [...list].sort((a, b) => (filter === 'pending' ? 1 : -1) * (Date.parse(a.submitted_at) - Date.parse(b.submitted_at)));
}
export const pendingCount = (rows) => (rows || []).filter((r) => reviewState(r) === 'pending').length;
/** "★★★★☆" and the number, for a screen reader too */
export const stars = (n) => (n >= 1 && n <= 5 ? `${'★'.repeat(n)}${'☆'.repeat(5 - n)}` : '');

// NIGHT-DIRECTSTAFF-2026-10-23b §3: fast approvals. A approves and moves on, R opens Reject, N skips to the next one: desktop
// only, never while typing in a field, never with Ctrl / Cmd / Alt. Reject takes a short reason from a list, more words, or both.
export const REJECT_REASONS = ['Not about working with an agency', 'Names a person or shares private details', 'Spam or a test',
  'Abusive or off-topic', 'A duplicate of another review'];
/** The reason sent: the one picked, then what staff typed ("Spam or a test: two in a row"); '' = none yet (Reject stays off). */
export const rejectReason = (picked, text) => [String(picked || '').trim(), String(text || '').trim()].filter(Boolean).join(': ');
/** keydown -> 'approve' | 'reject' | 'next' | null */
export function reviewKey(e, { desktop = true, active = null } = {}) {
  if (!desktop || !e || e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || isTyping(active)) return null;
  return { a: 'approve', r: 'reject', n: 'next', s: 'spam' }[String(e.key || '').toLowerCase()] || null;   // s: 10-28 §1, reject all likely spam
}
/** NIGHT-DIRECTSTAFF-2026-10-28c §5: nothing waiting, said plainly -- "No reviews waiting · 80 decided · last submission Oct 26, 2026" */
export function emptyLine(rows) {
  const all = rows || [];
  const decided = all.filter((r) => reviewState(r) !== 'pending').length;
  const at = all.map((r) => Date.parse(r.submitted_at)).filter(Number.isFinite);
  const last = at.length ? new Date(Math.max(...at)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  return ['No reviews waiting', `${decided} decided`, last ? `last submission ${last}` : 'none submitted yet'].join(' · ');
}
/** "12 waiting · oldest 9d" | emptyLine() */
export function waitingLine(rows, now = Date.now()) {
  const p = (rows || []).filter((r) => reviewState(r) === 'pending');
  if (!p.length) return emptyLine(rows);
  const at = p.map((r) => Date.parse(r.submitted_at)).filter(Number.isFinite);
  return `${p.length} waiting${at.length ? ` · oldest ${Math.max(0, Math.floor((now - Math.min(...at)) / 864e5))}d` : ''}`;
}

export const REVIEW_ERRORS = {
  'review:reason_required': 'Say why it is not published (up to 500 characters).',
  'review:not_found': 'That review is no longer there. Refresh the list.',
  'review:bad_decision': 'Choose Approve or Reject.',
  'review:bad_agency': 'Choose one of the agency pages, or none.',
};

/** Reads and the one write, over the signed-in staff client. */
export function makeReviewsApi(supabase) {
  return {
    /** -> { ready:false } before the migration; { ready:true, rows } after */
    async load() {
      // 10-28 §1: with the spam score when the hub has applied it; a column missing (42703) -> the columns before it
      let { data, error } = await supabase.from('reviews').select(REVIEW_COLS_SPAM).order('submitted_at', { ascending: false }).limit(1000);
      if (error && /42703/.test(String(error.code || '')) || (error && /spam_(score|reasons|cleared)/.test(String(error.message || '')))) {
        ({ data, error } = await supabase.from('reviews').select(REVIEW_COLS).order('submitted_at', { ascending: false }).limit(1000));
      }
      if (error) { if (missing(error)) return { ready: false }; throw new Error(error.message); }
      // NIGHT-DIRECTSTAFF-2026-10-24 §6: "Published" only once public.reviews_published (what the reviews site reads) has it
      let live = null;
      try { const v = await supabase.from('reviews_published').select('id').limit(5000); if (!v.error) live = new Set((v.data || []).map((x) => Number(x.id))); } catch (e) { live = null; }
      return { ready: true, rows: (data || []).map((r) => (live ? { ...r, published: live.has(Number(r.id)) } : r)) };
    },
    /** §6: ask the reviews site to publish now (the home window's repository_dispatch, through /api/reviews-publish).
     *  -> { dispatched, reason? } ; null when it could not even be asked (then the next publish run still takes it) */
    async publish(reviewId, fetchFn = (...a) => fetch(...a)) {
      try {
        const { data } = await supabase.auth.getSession();
        const token = data && data.session && data.session.access_token;
        if (!token) return null;
        const res = await fetchFn('/api/reviews-publish', { method: 'POST', headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
          body: JSON.stringify({ review_id: reviewId }) });
        return await res.json().catch(() => null);
      } catch (e) { return null; }
    },
    /** 10-28 §1: "Not spam" -- the review goes back into the list (review_not_spam: staff only, one timeline line) */
    async notSpam(id) {
      const { data, error } = await supabase.rpc('review_not_spam', { p_review_id: id });
      if (error) { const code = (/review:[a-z_]+/.exec(String(error.message || '')) || [])[0]; throw new Error(REVIEW_ERRORS[code] || error.message); }
      return data;
    },
    async decide(id, decision, reason = null, agencySlug = null) {
      const { data, error } = await supabase.rpc('review_decide', { p_review_id: id, p_decision: decision, p_reason: reason, p_agency_slug: agencySlug || null });
      if (error) {
        const code = (/review:[a-z_]+/.exec(String(error.message || '')) || [])[0];
        throw new Error(REVIEW_ERRORS[code] || error.message);
      }
      return data;
    },
  };
}
